import {
    AppInfo,
    BrandingSettings,
    ContactsSettings,
    FooterSettings,
    HoldingsSettings,
    Menu,
    PageRoute,
    TextsSettings,
} from '@types';
import { getAppInfoRequest } from '@api';
import usePageStore from '@/stores/page';
import { RouteRecordRaw } from 'vue-router';
import { router } from '@/plugins/1.router';

const useAppStore = defineStore('app', () => {
    // Instances
    const pageStore = usePageStore();

    // Data
    const isLoaded = ref<boolean>(false);
    const windowWidth = ref<number>(0);
    const windowHeight = ref<number>(0);
    const branding = ref<BrandingSettings | null>(null);
    const contacts = ref<ContactsSettings | null>(null);
    const menu = ref<Menu[]>([]);
    const footer = ref<FooterSettings | null>(null);
    const holdings = ref<HoldingsSettings | null>(null);
    const texts = ref<TextsSettings | null>();

    // Computed
    const isXsBreakpoint = computed<boolean>(() => windowWidth.value >= 376);
    const isSmBreakpoint = computed<boolean>(() => windowWidth.value >= 640);
    const isMdBreakpoint = computed<boolean>(() => windowWidth.value >= 768);
    const isLgBreakpoint = computed<boolean>(() => windowWidth.value >= 1024);
    const isXlBreakpoint = computed<boolean>(() => windowWidth.value >= 1280);
    const is2XlBreakpoint = computed<boolean>(() => windowWidth.value >= 1536);
    const is3XlBreakpoint = computed<boolean>(() => windowWidth.value >= 1920);
    const is4XlBreakpoint = computed<boolean>(() => windowWidth.value >= 2560);

    // Methods
    const getInfo = (): Promise<AppInfo> => {
        return new Promise((resolve, reject) => {
            getAppInfoRequest()
                .then(({data: {data}}) => {
                    branding.value = data.branding;
                    contacts.value = data.contacts;
                    menu.value = data.menu;
                    footer.value = data.footer;
                    holdings.value = data.holdings;
                    texts.value = data.texts;

                    registerMenuRoutes(data.pages);

                    resolve(data);
                })
                .catch(error => reject(error));
        });
    };

    const registerMenuRoutes = (pages: PageRoute[]): void => {
        const routes = generateMenuRoutes(pages);

        for (let i = 0; i < routes.length; i++) {
            router.addRoute('main-layout', routes[i]);
        }

        router.addRoute('main-layout', {
            path: '/:pathMatch(.*)*',
            name: 'page-not-found',
            component: () => import('@/pages/not-found.vue'),
            meta: {
                title: '404',
                type: 'system',
            },
        });

        router.replace(router.currentRoute.value.fullPath);
    };

    const generateMenuRoutes = (items: PageRoute[], parentNames: string[] = []): RouteRecordRaw[] => {
        const routes: RouteRecordRaw[] = [];

        if (!items.length) {
            return routes;
        }

        for (let i = 0; i < items.length; i++) {
            const item = items[i];

            const names: string[] = [...parentNames, item.slug];

            const data: RouteRecordRaw = {
                path: item.slug,
                name: !item.slug ? 'homepage' : names.join('_'),
                meta: Object.assign({}, {
                    title: item.title,
                    slug: item.slug,
                }, item.meta),
                component: () => import('@/pages/page/Page.vue'),
                beforeEnter: pageStore.beforePageEnter,
                children: generateMenuRoutes(item.items, names),
            };

            if (item.meta.redirect) {
                data.redirect = item.meta.redirect;

                delete data.component;
                delete data.beforeEnter;
            }

            routes.push(data);
        }

        return routes;
    };

    const setLoaded = (loaded: boolean): void => {
        isLoaded.value = loaded;
    };

    const updateWindowWidth = (width: number): void => {
        windowWidth.value = width;
    };

    const updateWindowHeight = (height: number): void => {
        windowHeight.value = height;
    };

    return {
        windowWidth,
        windowHeight,
        isLoaded,
        branding,
        contacts,
        menu,
        footer,
        holdings,
        texts,
        isXsBreakpoint,
        isSmBreakpoint,
        isMdBreakpoint,
        isLgBreakpoint,
        isXlBreakpoint,
        is2XlBreakpoint,
        is3XlBreakpoint,
        is4XlBreakpoint,
        getInfo,
        setLoaded,
        updateWindowWidth,
        updateWindowHeight,
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useAppStore, import.meta.hot));
}

export default useAppStore;
